<script setup>
import { computed } from "vue";
import moment from "moment";
import "moment/locale/es";


const props = defineProps({
  plans: { type: [Array, Object], default: null },
  month: { type: Object, required: true },
});

const emit = defineEmits(["click-plan", "prev-month", "next-month"]);

const WEEK_DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const STATUS_STYLE = {
  PENDIENTE:  { color: "#475569", bg: "#f8fafc", border: "#cbd5e1" },
  EN_CURSO:   { color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" },
  FINALIZADO: { color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
  CANCELADO:  { color: "#7c6d86", bg: "#f8f6fa", border: "#cfc7d4" },
  INCIDENCIA: { color: "#c2410c", bg: "#fff7ed", border: "#fdba74" },
  ERROR:      { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
};

const monthLabel = computed(() => {
  moment.locale("es");
  return props.month.format("MMMM YYYY");
});

const plansList = computed(() => {
  const p = props.plans;
  if (!p) return [];
  if (Array.isArray(p)) return p;
  if (Array.isArray(p.data)) return p.data;
  if (Array.isArray(p.items)) return p.items;
  return [];
});

const calendarDays = computed(() => {
  const start = props.month.clone().startOf("month").startOf("isoWeek");
  const end = props.month.clone().endOf("month").endOf("isoWeek");
  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    days.push({
      date: cursor.clone(),
      key: cursor.format("YYYY-MM-DD"),
      isCurrentMonth: cursor.isSame(props.month, "month"),
      isToday: cursor.isSame(moment(), "day"),
      dayNum: cursor.format("D"),
    });
    cursor.add(1, "day");
  }
  return days;
});

const plansByDate = computed(() => {
  const map = {};
  for (const plan of plansList.value) {
    if (!plan?.date) continue;
    const key = moment(plan.date).format("YYYY-MM-DD");
    if (!map[key]) map[key] = [];
    map[key].push(plan);
  }
  return map;
});

function plansForDay(key) {
  return plansByDate.value[key] || [];
}

function hasPlans(key) {
  return plansForDay(key).length > 0;
}

function statusStyle(status) {
  return STATUS_STYLE[status?.toUpperCase()] || STATUS_STYLE.PENDIENTE;
}

function statusLabel(status) {
  const labels = { PENDIENTE: "Borrador", EN_CURSO: "En ruta", FINALIZADO: "Finalizado" };
  return labels[status?.toUpperCase()] || status;
}

function typeLabel(type) {
  const normalized = String(type || "").trim().toUpperCase();
  return normalized;
}

function countValue(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
}

function totalStops(plan) {
  return (
    countValue(plan?.route_stops_pending) +
    countValue(plan?.route_stops_finalized) +
    countValue(plan?.route_stops_incidence)
  );
}
</script>

<template>
  <div class="plans-calendar">
    <div class="cal-nav">
      <div class="cal-nav-center">
        <button class="cal-nav-btn" aria-label="Mes anterior" @click="emit('prev-month')">
          <i class="fa fa-chevron-left"></i>
        </button>
        <span class="cal-nav-label">{{ monthLabel }}</span>
        <button class="cal-nav-btn" aria-label="Mes siguiente" @click="emit('next-month')">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <div class="cal-grid cal-header">
      <div v-for="day in WEEK_DAYS" :key="day" class="cal-day-header">{{ day }}</div>
    </div>

    <div class="cal-grid cal-body">
      <div
        v-for="day in calendarDays"
        :key="day.key"
        class="cal-day"
        :class="{
          'cal-day--other': !day.isCurrentMonth,
          'cal-day--today': day.isToday,
          'cal-day--has-plans': hasPlans(day.key),
        }"
      >
        <div class="cal-day-top">
          <span class="cal-day-num" :class="{ 'cal-day-num--today': day.isToday }">
            {{ day.dayNum }}
          </span>
        </div>
        <div class="cal-plans">
          <button
            v-for="plan in plansForDay(day.key)"
            :key="plan._id"
            class="cal-plan-pill"
            :style="{
              color: statusStyle(plan.status).color,
              background: statusStyle(plan.status).bg,
              borderColor: statusStyle(plan.status).border,
            }"
            @click="emit('click-plan', plan)"
          >
            <span class="cal-plan-main">
              <span class="cal-plan-name">{{ plan.name }}</span>
              <span class="cal-plan-status">{{ statusLabel(plan.status) }}</span>
            </span>
            <span class="cal-plan-meta">
              <span v-if="typeLabel(plan.type)">{{ typeLabel(plan.type) }}</span>
              <span v-if="typeLabel(plan.type) && totalStops(plan)" aria-hidden="true">
                ·
              </span>
              <span v-if="totalStops(plan)">{{ totalStops(plan) }} paradas</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.plans-calendar {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 2px 8px;
}

/* — navegación — */
.cal-nav {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 2px 0 12px;
}

.cal-nav-center {
  align-items: center;
  display: flex;
  gap: 4px;
}

.cal-nav-label {
  color: #1e293b;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  min-width: 150px;
  text-align: center;
  text-transform: capitalize;
}

.cal-nav-btn {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #94a3b8;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.7rem;
  height: 26px;
  justify-content: center;
  transition: background 120ms ease, color 120ms ease;
  width: 26px;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
}


/* — grid — */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.cal-body {
  flex: 1 1 auto;
  gap: 8px;
  grid-auto-rows: 1fr;
  min-height: 0;
}

.cal-header {
  margin-bottom: 8px;
}

.cal-day-header {
  color: var(--color-text-primary);
  font-size: 0.67rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  padding: 4px 4px 0px;
  text-align: center;
  text-transform: uppercase;
}

/* — celdas — */
.cal-day {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  min-height: 80px;
  overflow-y: auto;
  padding: 6px 5px 5px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
}

.cal-day--has-plans {
  border-color: #cbd5e1;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78);
}

.cal-day--today {
  border-color: #cbd5e1;
  background: color-mix(in srgb, var(--primary) 4%, #ffffff);
}

.cal-day--other {
  background: #fafbfc;

  .cal-day-num {
    color: #c5ccd6;
  }
}


/* — número de día — */
.cal-day-top {
  align-items: center;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 5px;
}

.cal-day-num {
  align-items: center;
  color: var(--color-text-primary);
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 600;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.cal-day-num--today {
  background:var(--color-text-primary);
  border-radius: 30%;
  color: #ffffff;
  font-size: 0.72rem;
}

/* — planes — */
.cal-plans {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cal-plan-pill {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  letter-spacing: 0;
  min-width: 0;
  padding: 5px 7px 5px 6px;
  text-align: left;
  transition: filter 140ms ease, transform 120ms ease, box-shadow 140ms ease;
  width: 100%;

  &:hover {
    filter: brightness(0.96);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
  }
}

.cal-plan-main,
.cal-plan-meta {
  align-items: center;
  display: flex;
  gap: 6px;
  min-width: 0;
  width: 100%;
}

.cal-plan-name {
  color: var(--color-text-primary);
  display: block;
  flex: 1 1 auto;
  font-size: 0.715rem;
  font-weight: 500;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-plan-status {
  color: currentColor;
  flex: 0 0 auto;
  font-size: 0.58rem;
  font-weight: 500;
  line-height: 1;
  max-width: 66px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.cal-plan-meta {
  color: var(--color-text-secondary);
  font-size: 0.62rem;
  font-weight: 500;
  line-height: 1;
}

@media (max-width: 768px) {
  .cal-day {
    min-height: 72px;
    padding: 5px 4px;
  }

  .cal-plan-status,
  .cal-plan-meta {
    display: none;
  }

  .cal-plan-pill {
    padding: 5px 7px 5px 9px;
  }
}

</style>
